import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  CUSTOMER_REPORT_TOKENS,
  DAY_OF_WEEK_LABELS,
  DEFAULT_CUSTOMER_REPORT_HTML,
  DEFAULT_CUSTOMER_REPORT_SUBJECT,
  type CreateCustomerReportInput,
  type CustomerReport,
  type CustomerReportEnrollment,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal, type ModalTab } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useCustomer } from '../customers/api.js';
import { useContacts } from '../contacts/api.js';
import {
  useCreateCustomerReport,
  useCustomerReportMemberOptions,
  useCustomerReportPreview,
  useCustomerReportSendPlan,
  useDeleteCustomerReport,
  useSendCustomerReport,
  useSetCustomerReportEnrollments,
  useUpdateCustomerReport,
} from './api.js';

interface CustomerReportFormModalProps {
  isOpen: boolean;
  report: CustomerReport | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  description: string;
  subject: string;
  html: string;
  includeProjects: boolean;
  includePayg: boolean;
  includeAgreements: boolean;
  includeTickets: boolean;
  closedTicketDays: string;
  dayOfWeek: number;
  hourOfDay: number;
  enabled: boolean;
  skipWhenNoData: boolean;
  testMode: boolean;
  testRecipients: string;
}

interface RosterRow {
  customerId: string;
  customerName: string;
  contactIds: string[];
  memberIds: string[];
}

function emptyForm(): FormState {
  return {
    name: '',
    description: '',
    subject: DEFAULT_CUSTOMER_REPORT_SUBJECT,
    html: DEFAULT_CUSTOMER_REPORT_HTML,
    includeProjects: true,
    includePayg: true,
    includeAgreements: true,
    includeTickets: true,
    closedTicketDays: '7',
    dayOfWeek: 1,
    hourOfDay: 7,
    enabled: false,
    skipWhenNoData: false,
    testMode: false,
    testRecipients: '',
  };
}

function toFormState(report: CustomerReport | null): FormState {
  if (!report) return emptyForm();
  return {
    name: report.name,
    description: report.description ?? '',
    subject: report.subject,
    html: report.html,
    includeProjects: report.includeProjects,
    includePayg: report.includePayg,
    includeAgreements: report.includeAgreements,
    includeTickets: report.includeTickets,
    closedTicketDays: String(report.closedTicketDays),
    dayOfWeek: report.dayOfWeek,
    hourOfDay: report.hourOfDay,
    enabled: report.enabled,
    skipWhenNoData: report.skipWhenNoData,
    testMode: report.testMode,
    testRecipients: report.testRecipients,
  };
}

function toRoster(enrollments: CustomerReportEnrollment[]): RosterRow[] {
  return enrollments.map((e) => ({
    customerId: e.customerId,
    customerName: e.customerName,
    contactIds: e.recipients.filter((r) => r.type === 'contact').map((r) => r.refId),
    memberIds: e.recipients.filter((r) => r.type === 'member').map((r) => r.refId),
  }));
}

/** Stable signature of a roster row (customer + its recipients) for change detection. */
function rosterKey(row: RosterRow): string {
  return [
    row.customerId,
    [...row.contactIds].sort().join(','),
    [...row.memberIds].sort().join(','),
  ].join('|');
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) result[detail.path] = detail.message;
    return result;
  }
  return {};
}

const TABS: ModalTab[] = [
  { id: 'details', label: 'Details' },
  { id: 'template', label: 'Template' },
  { id: 'customers', label: 'Customers' },
  { id: 'preview', label: 'Preview' },
];

export function CustomerReportFormModal({
  isOpen,
  report: reportProp,
  onClose,
}: CustomerReportFormModalProps): JSX.Element {
  const [report, setReport] = useState<CustomerReport | null>(reportProp);
  const isEdit = report !== null;
  const saveMode = useSaveMode();
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState<FormState>(() => toFormState(report));
  const [roster, setRoster] = useState<RosterRow[]>(() => toRoster(report?.enrollments ?? []));
  // Snapshot of the last persisted roster, used to flag unsaved enrollment changes.
  const [savedRoster, setSavedRoster] = useState<RosterRow[]>(() =>
    toRoster(report?.enrollments ?? []),
  );
  const [previewCustomerId, setPreviewCustomerId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateCustomerReport();
  const updateMutation = useUpdateCustomerReport();
  const deleteMutation = useDeleteCustomerReport();
  const enrollMutation = useSetCustomerReportEnrollments();
  const sendMutation = useSendCustomerReport();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetEnroll = enrollMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setReport(reportProp);
    }
  }, [isOpen, reportProp]);

  useEffect(() => {
    if (isOpen) {
      setTab('details');
      setForm(toFormState(report));
      setRoster(toRoster(report?.enrollments ?? []));
      setSavedRoster(toRoster(report?.enrollments ?? []));
      setPreviewCustomerId(report?.enrollments[0]?.customerId ?? null);
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
      resetEnroll();
    }
  }, [isOpen, report, resetSaveMode, resetCreate, resetUpdate, resetEnroll]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError = mutation.isError
    ? mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : !(mutation.error instanceof ApiError)
        ? 'Failed to save the report.'
        : null
    : null;

  const handleSubmit = (event?: FormEvent): void => {
    event?.preventDefault();
    const payload: CreateCustomerReportInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      subject: form.subject.trim() || DEFAULT_CUSTOMER_REPORT_SUBJECT,
      html: form.html,
      includeProjects: form.includeProjects,
      includePayg: form.includePayg,
      includeAgreements: form.includeAgreements,
      includeTickets: form.includeTickets,
      closedTicketDays: Number.parseInt(form.closedTicketDays, 10) || 7,
      dayOfWeek: form.dayOfWeek,
      hourOfDay: form.hourOfDay,
      enabled: form.enabled,
      skipWhenNoData: form.skipWhenNoData,
      testMode: form.testMode,
      testRecipients: form.testRecipients.trim(),
    };
    if (isEdit) {
      updateMutation.mutate(
        { id: report.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setReport(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!report) return;
    deleteMutation.mutate(report.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  const addCustomer = (row: RosterRow): void => {
    setRoster((rows) =>
      rows.some((r) => r.customerId === row.customerId) ? rows : [...rows, row],
    );
  };

  const removeCustomer = (customerId: string): void => {
    setRoster((rows) => rows.filter((r) => r.customerId !== customerId));
  };

  const setContacts = (customerId: string, contactIds: string[]): void => {
    setRoster((rows) => rows.map((r) => (r.customerId === customerId ? { ...r, contactIds } : r)));
  };

  const setMembers = (customerId: string, memberIds: string[]): void => {
    setRoster((rows) => rows.map((r) => (r.customerId === customerId ? { ...r, memberIds } : r)));
  };

  const saveRoster = (): void => {
    if (!report) return;
    const snapshot = roster;
    enrollMutation.mutate(
      {
        id: report.id,
        input: {
          enrollments: roster.map((r) => ({
            customerId: r.customerId,
            contactIds: r.contactIds,
            memberIds: r.memberIds,
          })),
        },
      },
      { onSuccess: () => setSavedRoster(snapshot) },
    );
  };

  // Per-customer and overall "unsaved enrollment changes" detection.
  const savedKeys = useMemo(() => new Set(savedRoster.map(rosterKey)), [savedRoster]);
  const unsavedCustomerIds = useMemo(
    () => new Set(roster.filter((r) => !savedKeys.has(rosterKey(r))).map((r) => r.customerId)),
    [roster, savedKeys],
  );
  const enrollmentsDirty = roster.length !== savedRoster.length || unsavedCustomerIds.size > 0;

  const footer = (
    <FormActions
      onSubmit={handleSubmit}
      saveMode={saveMode}
      isPending={mutation.isPending}
      disabled={!form.name.trim()}
      onCancel={onClose}
      leading={
        isEdit ? (
          <Button
            variant="ghost"
            className="mr-auto text-rose-600 hover:bg-rose-50"
            onClick={() => setConfirmOpen(true)}
          >
            Delete report
          </Button>
        ) : null
      }
    />
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit customer report' : 'New customer report'}
        size="wide"
        fillHeight={isEdit}
        tabs={isEdit ? TABS : TABS.slice(0, 2)}
        activeTabId={tab}
        onTabChange={setTab}
        footer={footer}
      >
        {generalError ? (
          <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {generalError}
          </p>
        ) : null}

        {tab === 'details' ? (
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              error={fieldErrors.name}
            />
            <TextField
              label="Recently-closed tickets window (days)"
              type="number"
              min={0}
              value={form.closedTicketDays}
              onChange={(e) => setForm((f) => ({ ...f, closedTicketDays: e.target.value }))}
              error={fieldErrors.closedTicketDays}
            />
            <div className="md:col-span-2">
              <TextAreaField
                label="Description"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <SelectField
              label="Send day"
              value={String(form.dayOfWeek)}
              onChange={(e) => setForm((f) => ({ ...f, dayOfWeek: Number(e.target.value) }))}
              options={DAY_OF_WEEK_LABELS.map((label, i) => ({ value: String(i), label }))}
            />
            <SelectField
              label="Send hour (billing time)"
              value={String(form.hourOfDay)}
              onChange={(e) => setForm((f) => ({ ...f, hourOfDay: Number(e.target.value) }))}
              options={Array.from({ length: 24 }, (_, h) => ({
                value: String(h),
                label: `${String(h).padStart(2, '0')}:00`,
              }))}
            />
            <div className="md:col-span-2 grid gap-2 rounded-lg border border-slate-200 p-3">
              <p className="text-sm font-medium text-slate-700">Sections to include</p>
              <CheckboxField
                label="Open projects (budget & hours)"
                checked={form.includeProjects}
                onChange={(e) => setForm((f) => ({ ...f, includeProjects: e.target.checked }))}
              />
              <CheckboxField
                label="Pay-As-You-Go-Support Agreements"
                checked={form.includePayg}
                onChange={(e) => setForm((f) => ({ ...f, includePayg: e.target.checked }))}
              />
              <CheckboxField
                label="Managed services agreements"
                checked={form.includeAgreements}
                onChange={(e) => setForm((f) => ({ ...f, includeAgreements: e.target.checked }))}
              />
              <CheckboxField
                label="Open & recently-closed tickets"
                checked={form.includeTickets}
                onChange={(e) => setForm((f) => ({ ...f, includeTickets: e.target.checked }))}
              />
            </div>
            <div className="md:col-span-2">
              <CheckboxField
                label="Enabled — send automatically on the weekly schedule"
                checked={form.enabled}
                onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
            </div>
            <div className="md:col-span-2">
              <CheckboxField
                label="Skip customers with no activity"
                checked={form.skipWhenNoData}
                onChange={(e) => setForm((f) => ({ ...f, skipWhenNoData: e.target.checked }))}
              />
              <p className="mt-1 text-xs text-slate-600">
                When on, a customer with no hours logged this month or last week is skipped
                instead of being emailed an empty report. Agreements, projects and open
                tickets with no hours behind them do not count as activity.
              </p>
            </div>
            <div className="md:col-span-2 grid gap-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
              <CheckboxField
                label="Test Mode"
                checked={form.testMode}
                onChange={(e) => setForm((f) => ({ ...f, testMode: e.target.checked }))}
              />
              <p className="text-xs text-slate-600">
                When on, “Send now” and scheduled sends do NOT email the customers’ recipients. A
                separate email is sent for each enrolled customer to the test addresses below.
              </p>
              <TextField
                label="Test email recipients"
                placeholder="me@example.com, qa@example.com"
                value={form.testRecipients}
                onChange={(e) => setForm((f) => ({ ...f, testRecipients: e.target.value }))}
                disabled={!form.testMode}
              />
            </div>
          </div>
        ) : null}

        {tab === 'template' ? (
          <div className="grid gap-4">
            <TextField
              label="Email subject"
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            />
            <TextAreaField
              label="HTML template"
              rows={18}
              value={form.html}
              onChange={(e) => setForm((f) => ({ ...f, html: e.target.value }))}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' }}
            />
            <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-600">
              <p className="mb-1 font-medium text-slate-700">Available tokens</p>
              <p className="leading-6">
                {CUSTOMER_REPORT_TOKENS.map((t) => (
                  <code
                    key={t}
                    className="mr-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 font-mono"
                  >{`{{${t}}}`}</code>
                ))}
              </p>
              <p className="mt-2">
                Use <code className="font-mono">{'{{{projectsTable}}}'}</code> (triple braces) for
                generated tables, and{' '}
                <code className="font-mono">{'{{#includeProjects}}…{{/includeProjects}}'}</code> to
                gate a whole section.
              </p>
            </div>
          </div>
        ) : null}

        {tab === 'customers' && isEdit ? (
          <CustomersTab
            roster={roster}
            onAdd={addCustomer}
            onRemove={removeCustomer}
            onSetContacts={setContacts}
            onSetMembers={setMembers}
            onSave={saveRoster}
            saving={enrollMutation.isPending}
            saved={enrollMutation.isSuccess && !enrollmentsDirty}
            dirty={enrollmentsDirty}
            unsavedIds={unsavedCustomerIds}
          />
        ) : null}

        {tab === 'preview' && isEdit ? (
          <PreviewTab
            reportId={report.id}
            enrollments={report.enrollments}
            customerId={previewCustomerId}
            onSelectCustomer={setPreviewCustomerId}
            onSend={() => sendMutation.mutate(report.id)}
            sending={sendMutation.isPending}
            sendResult={sendMutation.data?.items.length ?? null}
          />
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete customer report"
        message="This permanently deletes the report and its customer enrollments. This cannot be undone."
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Customers (enrollment roster) tab
// ---------------------------------------------------------------------------

interface CustomersTabProps {
  roster: RosterRow[];
  onAdd: (row: RosterRow) => void;
  onRemove: (customerId: string) => void;
  onSetContacts: (customerId: string, contactIds: string[]) => void;
  onSetMembers: (customerId: string, memberIds: string[]) => void;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
  dirty: boolean;
  unsavedIds: Set<string>;
}

interface MemberOption {
  id: string;
  name: string;
  email: string | null;
}

function CustomersTab({
  roster,
  onAdd,
  onRemove,
  onSetContacts,
  onSetMembers,
  onSave,
  saving,
  saved,
  dirty,
  unsavedIds,
}: CustomersTabProps): JSX.Element {
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(null);
  const membersQuery = useCustomerReportMemberOptions();
  const memberOptions: MemberOption[] = (membersQuery.data ?? []).map((m) => ({
    id: m.id,
    name: m.displayName,
    email: m.email,
  }));

  // Keep the enrolled list alphabetical regardless of add order.
  const sortedRoster = [...roster].sort((a, b) =>
    (a.customerName || '').localeCompare(b.customerName || ''),
  );

  const enrolledIds = new Set(roster.map((r) => r.customerId));

  return (
    <div className="grid gap-4">
      <div className="flex items-end gap-2">
        <div className="w-80">
          <CustomerSelectField
            label="Add a customer"
            value=""
            onChange={(id) => {
              if (id && !enrolledIds.has(id)) setPendingCustomerId(id);
            }}
            placeholder="Search customers…"
          />
        </div>
        <Button variant="primary" onClick={onSave} isLoading={saving} disabled={saving}>
          Save enrollments
        </Button>
        {dirty ? (
          <span className="inline-flex animate-pulse items-center rounded-full bg-rose-600 px-3 py-1 text-sm font-semibold text-white shadow-sm">
            Enrollments not saved
          </span>
        ) : saved && !saving ? (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700">
            Saved
          </span>
        ) : null}
      </div>

      {roster.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
          No customers enrolled yet. Add a customer above, choose its recipients in the pop-up, then
          Save enrollments.
        </p>
      ) : (
        <div className="grid gap-3">
          {sortedRoster.map((row) => (
            <EnrollmentRow
              key={row.customerId}
              row={row}
              memberOptions={memberOptions}
              unsaved={unsavedIds.has(row.customerId)}
              onRemove={() => onRemove(row.customerId)}
              onSetContacts={(ids) => onSetContacts(row.customerId, ids)}
              onSetMembers={(ids) => onSetMembers(row.customerId, ids)}
            />
          ))}
        </div>
      )}

      <AddCustomerDialog
        customerId={pendingCustomerId}
        memberOptions={memberOptions}
        onClose={() => setPendingCustomerId(null)}
        onAdd={(row) => {
          onAdd(row);
          setPendingCustomerId(null);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-customer dialog: pick a new customer's recipients without scrolling the list
// ---------------------------------------------------------------------------

interface AddCustomerDialogProps {
  customerId: string | null;
  memberOptions: MemberOption[];
  onClose: () => void;
  onAdd: (row: RosterRow) => void;
}

function AddCustomerDialog({
  customerId,
  memberOptions,
  onClose,
  onAdd,
}: AddCustomerDialogProps): JSX.Element {
  const [contactIds, setContactIds] = useState<string[]>([]);
  const [memberIds, setMemberIds] = useState<string[]>([]);

  useEffect(() => {
    if (customerId) {
      setContactIds([]);
      setMemberIds([]);
    }
  }, [customerId]);

  const customerQuery = useCustomer(customerId ?? '');
  const contactsQuery = useContacts({
    customerId: customerId ?? '',
    pageSize: 200,
    isActive: true,
  });
  const contacts = contactsQuery.data?.data ?? [];
  const customerName = customerQuery.data?.name ?? 'Customer';

  const toggleContact = (id: string): void =>
    setContactIds((ids) => (ids.includes(id) ? ids.filter((c) => c !== id) : [...ids, id]));
  const toggleMember = (id: string): void =>
    setMemberIds((ids) => (ids.includes(id) ? ids.filter((m) => m !== id) : [...ids, id]));

  const handleAdd = (): void => {
    if (!customerId) return;
    onAdd({ customerId, customerName, contactIds, memberIds });
  };

  const footer = (
    <div className="flex w-full items-center justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleAdd}>
        Add to enrollments
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={customerId !== null}
      onClose={onClose}
      title={`Add ${customerName}`}
      size="md"
      footer={footer}
    >
      <div className="grid gap-4">
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
            Customer contacts
          </p>
          {contactsQuery.isLoading ? (
            <Spinner />
          ) : contacts.length === 0 ? (
            <p className="text-sm text-slate-500">This customer has no active contacts.</p>
          ) : (
            <div className="grid gap-1 sm:grid-cols-2">
              {contacts.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={contactIds.includes(c.id)}
                    onChange={() => toggleContact(c.id)}
                  />
                  <span>
                    {`${c.firstName} ${c.lastName ?? ''}`.trim()}
                    {c.email ? <span className="text-slate-400"> · {c.email}</span> : null}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {memberOptions.length > 0 ? (
          <div className="border-t border-slate-100 pt-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              Members (internal copy)
            </p>
            <div className="grid gap-1 sm:grid-cols-2">
              {memberOptions.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={memberIds.includes(m.id)}
                    onChange={() => toggleMember(m.id)}
                  />
                  <span>
                    {m.name}
                    {m.email ? <span className="text-slate-400"> · {m.email}</span> : null}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

interface EnrollmentRowProps {
  row: RosterRow;
  memberOptions: MemberOption[];
  unsaved: boolean;
  onRemove: () => void;
  onSetContacts: (contactIds: string[]) => void;
  onSetMembers: (memberIds: string[]) => void;
}

function EnrollmentRow({
  row,
  memberOptions,
  unsaved,
  onRemove,
  onSetContacts,
  onSetMembers,
}: EnrollmentRowProps): JSX.Element {
  const contactsQuery = useContacts({ customerId: row.customerId, pageSize: 200, isActive: true });
  const contacts = contactsQuery.data?.data ?? [];
  // Resolve the display name for freshly-added rows (existing enrollments already carry it).
  const customerQuery = useCustomer(row.customerName ? '' : row.customerId);
  const customerName = row.customerName || customerQuery.data?.name || 'Customer';
  const recipientCount = row.contactIds.length + row.memberIds.length;

  const toggle = (id: string): void => {
    onSetContacts(
      row.contactIds.includes(id)
        ? row.contactIds.filter((c) => c !== id)
        : [...row.contactIds, id],
    );
  };

  const toggleMember = (id: string): void => {
    onSetMembers(
      row.memberIds.includes(id) ? row.memberIds.filter((m) => m !== id) : [...row.memberIds, id],
    );
  };

  return (
    <div
      className={`rounded-lg border p-3 ${unsaved ? 'border-rose-300 bg-rose-50/40' : 'border-slate-200'}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <span>
            {customerName}{' '}
            <span className="text-slate-400">
              ({recipientCount} recipient{recipientCount === 1 ? '' : 's'})
            </span>
          </span>
          {unsaved ? <Badge tone="red">Unsaved</Badge> : null}
        </p>
        <Button variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={onRemove}>
          Remove
        </Button>
      </div>
      {contactsQuery.isLoading ? (
        <Spinner />
      ) : contacts.length === 0 ? (
        <p className="text-sm text-slate-500">This customer has no active contacts.</p>
      ) : (
        <div className="grid gap-1 sm:grid-cols-2">
          {contacts.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={row.contactIds.includes(c.id)}
                onChange={() => toggle(c.id)}
              />
              <span>
                {`${c.firstName} ${c.lastName ?? ''}`.trim()}
                {c.email ? <span className="text-slate-400"> · {c.email}</span> : null}
              </span>
            </label>
          ))}
        </div>
      )}
      {memberOptions.length > 0 ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
            Members (internal copy)
          </p>
          <div className="grid gap-1 sm:grid-cols-2">
            {memberOptions.map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={row.memberIds.includes(m.id)}
                  onChange={() => toggleMember(m.id)}
                />
                <span>
                  {m.name}
                  {m.email ? <span className="text-slate-400"> · {m.email}</span> : null}
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview tab
// ---------------------------------------------------------------------------

interface PreviewTabProps {
  reportId: string;
  enrollments: CustomerReportEnrollment[];
  customerId: string | null;
  onSelectCustomer: (id: string) => void;
  onSend: () => void;
  sending: boolean;
  sendResult: number | null;
}

function PreviewTab({
  reportId,
  enrollments,
  customerId,
  onSelectCustomer,
  onSend,
  sending,
  sendResult,
}: PreviewTabProps): JSX.Element {
  const previewQuery = useCustomerReportPreview(reportId, customerId, true);
  const preview = previewQuery.data;
  const planQuery = useCustomerReportSendPlan(reportId, enrollments.length > 0);
  const plan = planQuery.data ?? [];
  const willSendCount = plan.filter((p) => p.willSend).length;

  if (enrollments.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
        Enroll a customer on the Customers tab to preview their report.
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex items-end justify-between gap-2">
        <div className="w-72">
          <SelectField
            label="Preview for customer"
            value={customerId ?? ''}
            onChange={(e) => onSelectCustomer(e.target.value)}
            options={enrollments.map((e) => ({ value: e.customerId, label: e.customerName }))}
          />
        </div>
        <Button variant="primary" onClick={onSend} isLoading={sending} disabled={sending}>
          Send to all enrolled customers
        </Button>
      </div>

      {sendResult !== null ? (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          Processed {sendResult} customer{sendResult === 1 ? '' : 's'}.
        </p>
      ) : null}

      <div className="rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
          <h3 className="text-sm font-semibold text-slate-900">Who gets this report</h3>
          {planQuery.isLoading ? (
            <span className="text-xs text-slate-500">Checking…</span>
          ) : (
            <span className="text-xs text-slate-500">
              {willSendCount} of {plan.length} will be sent
            </span>
          )}
        </div>
        <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto">
          {plan.map((item) => (
            <li key={item.customerId}>
              <button
                type="button"
                onClick={() => onSelectCustomer(item.customerId)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                  item.customerId === customerId ? 'bg-brand-50' : ''
                }`}
              >
                <span className="truncate text-slate-800">{item.customerName}</span>
                <span className="flex shrink-0 items-center gap-2">
                  {item.reason ? (
                    <span className="text-xs text-slate-500">{item.reason}</span>
                  ) : null}
                  <Badge tone={item.willSend ? 'green' : 'amber'}>
                    {item.willSend ? 'Will be sent' : 'Will be skipped'}
                  </Badge>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {preview ? (
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span className="font-medium text-slate-700">Recipients:</span>
          {preview.recipients.length === 0 ? (
            <Badge tone="amber">No recipient contacts selected</Badge>
          ) : (
            preview.recipients.map((r) => (
              <Badge key={r} tone="gray">
                {r}
              </Badge>
            ))
          )}
        </div>
      ) : null}

      {previewQuery.isLoading ? (
        <Spinner />
      ) : preview ? (
        <iframe
          title="Report preview"
          srcDoc={preview.html}
          className="h-[60vh] w-full rounded-lg border border-slate-200 bg-white"
        />
      ) : (
        <p className="text-sm text-rose-600">Failed to render the preview.</p>
      )}
    </div>
  );
}
